import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Download, Brain, Target, Bot, Zap, ListChecks, MapPin, Shield, Coins, Dices,
  ChevronRight, Clock, Cpu, FileJson, MessageSquare, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { downloadMessageDebugLog } from "@/lib/message-debug";

interface MessageDebugDialogProps {
  msg: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FlowStep {
  key: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  summary?: string;
  timingMs?: number;
  details?: React.ReactNode;
}

function fmtMs(ms?: number): string | undefined {
  if (ms == null || Number.isNaN(ms)) return undefined;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * Comprehensive per-message debug visualizer (admins / world owners).
 * Reconstructs the AI process for the message as a vertical stepped timeline
 * from its metadata, plus an overview header and a raw-JSON section with a
 * one-click log download. Works for any message; non-AI messages show the
 * core fields and a "no AI process data" note.
 */
export function MessageDebugDialog({ msg, open, onOpenChange }: MessageDebugDialogProps) {
  const [rawOpen, setRawOpen] = useState(false);
  const md = (msg?.metadata ?? {}) as Record<string, any>;

  const isAi = msg?.role === "narrator" || msg?.role === "assistant";

  const classification = md.intent_classification;
  const directorNotes = md.director_notes;
  const subAgentSummary = md.sub_agent_summary;
  const subActions: any[] = Array.isArray(md.sub_actions) ? md.sub_actions : [];
  const agentsUsed: string[] = Array.isArray(md.agents_used) ? md.agents_used : [];
  const toolsCalled: number = typeof md.tools_called === "number" ? md.tools_called : 0;
  const stateChanges = md.state_changes as Record<string, any> | undefined;
  const mechanicResults: any[] = Array.isArray(md.mechanic_results) ? md.mechanic_results : [];
  const diceRolls: any[] = Array.isArray(md.dice_rolls) ? md.dice_rolls : [];
  const validity = md.validity_check;
  const inputTokens = md.input_tokens as number | undefined;
  const outputTokens = md.output_tokens as number | undefined;
  const creditsUsed = md.credits_used as number | undefined;
  const totalMs = (md.sub_agents?.total_execution_ms ?? md.execution_ms ?? md.total_execution_ms) as number | undefined;
  const models: string[] = Array.isArray(md.models)
    ? md.models
    : md.model
      ? [String(md.model)]
      : [];

  const steps = useMemo<FlowStep[]>(() => {
    const out: FlowStep[] = [];

    // 1. Player input / message received
    out.push({
      key: "input",
      title: isAi ? "Player input received" : "Message",
      icon: <MessageSquare className="w-3.5 h-3.5" />,
      color: "#64748b",
      summary: (msg?.content || "").slice(0, 120) || undefined,
    });

    // 2. Intent classification
    if (classification) {
      out.push({
        key: "classification",
        title: "Intent classification",
        icon: <Target className="w-3.5 h-3.5" />,
        color: "#6366f1",
        summary: classification.primary_intent,
        details: (
          <div className="space-y-1">
            <div className="flex flex-wrap gap-1">
              {classification.primary_intent && (
                <Badge variant="default" className="text-[10px] h-4">{classification.primary_intent}</Badge>
              )}
              {classification.secondary_intents?.map((s: string) => (
                <Badge key={s} variant="outline" className="text-[10px] h-4 text-muted-foreground">{s}</Badge>
              ))}
              {classification.echo_risk && (
                <Badge variant="outline" className="text-[10px] h-4 text-orange-500 border-orange-500/30">echo risk</Badge>
              )}
              {classification.is_inspection && (
                <Badge variant="outline" className="text-[10px] h-4 text-amber-500 border-amber-500/30">inspection</Badge>
              )}
              {classification.is_planning && (
                <Badge variant="outline" className="text-[10px] h-4 text-blue-500 border-blue-500/30">planning</Badge>
              )}
            </div>
            {classification.reasoning && (
              <p className="text-muted-foreground italic leading-snug">{classification.reasoning}</p>
            )}
          </div>
        ),
      });
    }

    // 3. Sub-agents / tools
    if (agentsUsed.length > 0 || subAgentSummary || toolsCalled > 0) {
      out.push({
        key: "agents",
        title: "Sub-agents & tools",
        icon: <Bot className="w-3.5 h-3.5" />,
        color: "#8b5cf6",
        summary: [
          agentsUsed.length ? `${agentsUsed.length} agent${agentsUsed.length > 1 ? "s" : ""}` : null,
          toolsCalled ? `${toolsCalled} tool${toolsCalled > 1 ? "s" : ""}` : null,
        ].filter(Boolean).join(" · ") || undefined,
        timingMs: totalMs,
        details: (
          <div className="space-y-1.5">
            {agentsUsed.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {agentsUsed.map((a) => (
                  <Badge key={a} variant="secondary" className="text-[10px] h-4 gap-1">
                    <Bot className="w-2.5 h-2.5" />{a}
                  </Badge>
                ))}
              </div>
            )}
            {subAgentSummary && (
              <pre className="text-[10px] text-foreground/70 whitespace-pre-wrap font-mono bg-muted/30 rounded p-2 max-h-40 overflow-auto">
                {typeof subAgentSummary === "string" ? subAgentSummary : JSON.stringify(subAgentSummary, null, 2)}
              </pre>
            )}
          </div>
        ),
      });
    }

    // 4. Sub-actions (compound breakdown)
    if (subActions.length > 0) {
      out.push({
        key: "subactions",
        title: "Sub-actions",
        icon: <ListChecks className="w-3.5 h-3.5" />,
        color: "#0ea5e9",
        summary: `${subActions.length} step${subActions.length > 1 ? "s" : ""}`,
        details: (
          <div className="space-y-0.5">
            {subActions.map((sa, i) => (
              <div key={i} className="flex items-start gap-1.5">
                <span className="text-primary font-mono shrink-0 text-[10px]">{i + 1}.</span>
                <span className="text-foreground/80">{typeof sa === "string" ? sa : `[${sa.intent}] ${sa.text}`}</span>
              </div>
            ))}
          </div>
        ),
      });
    }

    // 5. Director notes
    if (directorNotes) {
      out.push({
        key: "director",
        title: "Director decisions",
        icon: <Brain className="w-3.5 h-3.5" />,
        color: "#a855f7",
        summary: directorNotes.pacing_transition?.new_phase
          ? `pacing → ${directorNotes.pacing_transition.new_phase}`
          : undefined,
        details: (
          <div className="space-y-1">
            {directorNotes.narrative_guidance && (
              <p className="text-foreground/70">📝 {directorNotes.narrative_guidance}</p>
            )}
            {directorNotes.advance_turn !== undefined && (
              <div className="text-foreground/70">
                Turn advance: <span className={cn("font-medium", directorNotes.advance_turn ? "text-green-500" : "text-muted-foreground")}>{directorNotes.advance_turn ? "Yes" : "No"}</span>
              </div>
            )}
            {directorNotes.npc_intents?.length > 0 && (
              <div className="space-y-0.5">
                {directorNotes.npc_intents.map((npc: any, i: number) => (
                  <div key={i} className="text-foreground/70">
                    <span className="font-medium">{npc.name}:</span> <span className="italic">{npc.intent}</span>
                  </div>
                ))}
              </div>
            )}
            {directorNotes.inject_encounter && (
              <div className="text-amber-500/80">⚔️ Encounter: {directorNotes.inject_encounter.description}</div>
            )}
            {directorNotes.mercy_event && (
              <div className="text-green-500/80">🩹 Mercy: {directorNotes.mercy_event.description}</div>
            )}
            {directorNotes.escalation_event && (
              <div className="text-red-500/80">🔥 Escalation: {directorNotes.escalation_event.description}</div>
            )}
          </div>
        ),
      });
    }

    // 6. Mechanics / dice
    if (mechanicResults.length > 0 || diceRolls.length > 0) {
      out.push({
        key: "mechanics",
        title: "Mechanics & dice",
        icon: <Dices className="w-3.5 h-3.5" />,
        color: "#f59e0b",
        summary: [
          mechanicResults.length ? `${mechanicResults.length} mechanic${mechanicResults.length > 1 ? "s" : ""}` : null,
          diceRolls.length ? `${diceRolls.length} roll${diceRolls.length > 1 ? "s" : ""}` : null,
        ].filter(Boolean).join(" · ") || undefined,
        details: (
          <div className="space-y-1">
            {diceRolls.map((d, i) => (
              <div key={i} className="text-foreground/80 font-mono text-[11px]">
                {d.expression} = <span className="text-primary font-bold">{d.result}</span>
                {d.reason ? <span className="text-muted-foreground"> — {d.reason}</span> : null}
              </div>
            ))}
            {mechanicResults.length > 0 && (
              <pre className="text-[10px] text-foreground/70 whitespace-pre-wrap font-mono bg-muted/30 rounded p-2 max-h-32 overflow-auto">
                {JSON.stringify(mechanicResults, null, 2)}
              </pre>
            )}
          </div>
        ),
      });
    }

    // 7. Validity / anti-cheat
    if (validity && validity.is_valid === false) {
      out.push({
        key: "validity",
        title: "Anti-cheat violation",
        icon: <Shield className="w-3.5 h-3.5" />,
        color: "#ef4444",
        summary: validity.violation_type,
        details: validity.violation_details ? (
          <p className="text-red-400/80">{validity.violation_details}</p>
        ) : undefined,
      });
    }

    // 8. State changes (world mutations)
    if (stateChanges && Object.keys(stateChanges).length > 0) {
      out.push({
        key: "state",
        title: "State changes",
        icon: <MapPin className="w-3.5 h-3.5" />,
        color: "#22c55e",
        summary: `${Object.keys(stateChanges).length} field${Object.keys(stateChanges).length > 1 ? "s" : ""}`,
        details: (
          <div className="space-y-1">
            {Object.entries(stateChanges).map(([key, val]) => (
              <div key={key} className="flex flex-col gap-0.5">
                <span className="text-muted-foreground font-mono text-[10px]">{key}:</span>
                {typeof val === "string" ? (
                  <span className="text-foreground/80 break-all">{val}</span>
                ) : (
                  <pre className="text-[10px] text-foreground/80 whitespace-pre-wrap font-mono bg-muted/30 rounded p-1.5 max-h-32 overflow-auto">
                    {JSON.stringify(val, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        ),
      });
    }

    // 9. Final output
    if (isAi) {
      out.push({
        key: "output",
        title: "Narrator output",
        icon: <CheckCircle2 className="w-3.5 h-3.5" />,
        color: "#10b981",
        summary: (msg?.content || "").slice(0, 120) || undefined,
      });
    }

    return out;
  }, [msg, isAi, classification, directorNotes, subAgentSummary, subActions, agentsUsed, toolsCalled, stateChanges, mechanicResults, diceRolls, validity, totalMs]);

  const hasAiTrace = isAi && (classification || directorNotes || subActions.length > 0 || agentsUsed.length > 0 || subAgentSummary);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-hidden p-0 gap-0 w-full h-full sm:h-auto sm:w-auto flex flex-col">
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-border/60">
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Brain className="w-4 h-4 text-primary" />
            Message Debug
            <Badge variant="outline" className="text-[10px] h-5">{isAi ? "AI message" : "Player message"}</Badge>
          </DialogTitle>
          {/* Overview stats */}
          <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
            <Badge variant="secondary" className="text-[10px] h-5 gap-1 font-mono">id {String(msg?.id || "").slice(0, 8)}</Badge>
            {msg?.turn != null && <Badge variant="outline" className="text-[10px] h-5">turn {msg.turn}</Badge>}
            {md.flow && <Badge variant="outline" className="text-[10px] h-5">flow {String(md.flow)}</Badge>}
            {models.map((m) => (
              <Badge key={m} variant="outline" className="text-[10px] h-5 gap-1"><Cpu className="w-2.5 h-2.5" />{m}</Badge>
            ))}
            {fmtMs(totalMs) && (
              <Badge variant="outline" className="text-[10px] h-5 gap-1"><Clock className="w-2.5 h-2.5" />{fmtMs(totalMs)}</Badge>
            )}
            {(inputTokens || outputTokens) && (
              <Badge variant="outline" className="text-[10px] h-5 gap-1"><Zap className="w-2.5 h-2.5" />
                {inputTokens ?? 0}/{outputTokens ?? 0} tok
              </Badge>
            )}
            {creditsUsed != null && (
              <Badge variant="outline" className="text-[10px] h-5 gap-1"><Coins className="w-2.5 h-2.5" />
                {Number(creditsUsed) < 0.01 ? Number(creditsUsed).toFixed(4) : Number(creditsUsed).toFixed(2)} cr
              </Badge>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {/* Process timeline */}
          {!hasAiTrace && !isAi && (
            <p className="text-xs text-muted-foreground py-2">This is a player message — no AI process trace is recorded.</p>
          )}
          {isAi && !hasAiTrace && (
            <p className="text-xs text-muted-foreground py-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
              No AI process data was captured for this message.
            </p>
          )}

          <div className="relative pl-1">
            {steps.map((step, i) => (
              <div key={step.key} className="relative flex gap-3 pb-3 last:pb-0">
                {/* connector line */}
                {i < steps.length - 1 && (
                  <span className="absolute left-[11px] top-6 bottom-0 w-px bg-border" aria-hidden />
                )}
                {/* node */}
                <span
                  className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white"
                  style={{ backgroundColor: step.color }}
                >
                  {step.icon}
                </span>
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold">{step.title}</span>
                    {fmtMs(step.timingMs) && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                        <Clock className="w-2.5 h-2.5" />{fmtMs(step.timingMs)}
                      </span>
                    )}
                  </div>
                  {step.summary && (
                    <p className="text-[11px] text-muted-foreground line-clamp-2 leading-snug mt-0.5">{step.summary}</p>
                  )}
                  {step.details && <div className="mt-1 text-[11px]">{step.details}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Raw JSON */}
          <Collapsible open={rawOpen} onOpenChange={setRawOpen} className="mt-3 border-t border-border/60 pt-2">
            <CollapsibleTrigger className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors">
              <ChevronRight className={cn("w-3.5 h-3.5 transition-transform", rawOpen && "rotate-90")} />
              <FileJson className="w-3.5 h-3.5" />
              Raw metadata
            </CollapsibleTrigger>
            <CollapsibleContent>
              <pre className="mt-2 text-[10px] text-foreground/70 whitespace-pre-wrap font-mono bg-muted/30 rounded p-2 max-h-72 overflow-auto">
                {JSON.stringify(md, null, 2)}
              </pre>
            </CollapsibleContent>
          </Collapsible>
        </div>

        <div className="px-4 py-3 border-t border-border/60 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => downloadMessageDebugLog(msg)}>
            <Download className="w-3.5 h-3.5" />
            Download log
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
